/**
 * Types Practice Arena (Salve P1 T3).
 *
 * Cohérents avec le backend (PHP DTOs PracticeGameSummary / PracticeGameDetail
 * / PracticeFinishResult).
 */

export type PracticeCadence =
  | 'bullet'
  | 'blitz_3_2'
  | 'blitz_5_3'
  | 'rapid_15_10'
  | 'classique_30_0';

export type PracticeColor = 'white' | 'black' | 'random';

export type PracticeResult = 'win' | 'loss' | 'draw' | 'abandoned';

export interface PracticeTheme {
  slug: string;
  name_fr: string;
  description_fr: string;
  starting_fen: string | null;
  side_to_move: 'white' | 'black';
  sort_order: number;
}

export interface PracticeGameSummary {
  id: number;
  level_elo: number;
  cadence: PracticeCadence;
  color: PracticeColor;
  /**
   * Couleur effective résolue côté serveur (cf. migration 019 — Salve P2).
   * Toujours 'white' ou 'black' : la valeur 'random' n'est jamais persistée.
   */
  resolved_color: 'white' | 'black';
  theme_slug: string | null;
  /**
   * FEN initial du thème associé (LEFT JOIN `practice_themes` côté backend).
   * NULL si pas de thème ou si le thème démarre en position standard.
   */
  starting_fen: string | null;
  result: PracticeResult | null;
  moves_count: number;
  duration_ms: number;
  started_at: string;
  finished_at: string | null;
}

export interface PracticeMove {
  ply: number;
  san: string;
  uci: string;
  time_spent_ms: number;
  eval_cp: number | null;
}

export interface PracticeGameDetail {
  id: number;
  level_elo: number;
  cadence: PracticeCadence;
  color: PracticeColor;
  /**
   * Couleur effective résolue côté serveur (cf. migration 019 — Salve P2).
   * Toujours 'white' ou 'black' : la valeur 'random' n'est jamais persistée.
   * Source de vérité unique pour l'orientation du board en review.
   */
  resolved_color: 'white' | 'black';
  theme_slug: string | null;
  /**
   * FEN initial du thème associé (LEFT JOIN `practice_themes` côté backend).
   * NULL si pas de thème ou si le thème démarre en position standard.
   */
  starting_fen: string | null;
  result: PracticeResult | null;
  moves_count: number;
  duration_ms: number;
  final_fen: string | null;
  pgn: string | null;
  started_at: string;
  finished_at: string | null;
  moves: PracticeMove[];
}

export interface PracticeFinishResult {
  game_id: number;
  result: PracticeResult;
  moves_count: number;
  duration_ms: number;
  xp_awarded: boolean;
  xp_amount: number;
  game_qualifies_for_badges: boolean;
  unlocked_badges: string[];
}

/**
 * Item badge retourné par `GET /api/v1/practice/badges` (Salve P1 T7).
 * `icon_glyph` réfère à un nom de composant Phosphor (cf. migration 018).
 */
export interface BadgeListItem {
  slug: string;
  name_fr: string;
  description_fr: string;
  icon_glyph: string;
  sort_order: number;
  unlocked: boolean;
  unlocked_at: string | null;
  game_id: number | null;
}

export interface BadgesResponse {
  count: number;
  unlocked_count: number;
  badges: BadgeListItem[];
}

export interface CreateGameParams {
  level_elo: number;
  cadence: PracticeCadence;
  color: PracticeColor;
  theme_slug?: string | null;
}

/**
 * Paramètres d'horloge dérivés de la cadence. `initialMs` = temps de base,
 * `incrementMs` = increment Fischer par coup.
 */
export interface CadenceTiming {
  initialMs: number;
  incrementMs: number;
  label: string;
}

export const CADENCE_TIMING: Record<PracticeCadence, CadenceTiming> = {
  bullet: { initialMs: 60_000, incrementMs: 0, label: '1+0 bullet' },
  blitz_3_2: { initialMs: 180_000, incrementMs: 2_000, label: '3+2 blitz' },
  blitz_5_3: { initialMs: 300_000, incrementMs: 3_000, label: '5+3 blitz' },
  rapid_15_10: { initialMs: 900_000, incrementMs: 10_000, label: '15+10 rapide' },
  classique_30_0: { initialMs: 1_800_000, incrementMs: 0, label: '30+0 classique' },
};
